import { Component, createRef, RefObject } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './eyedropper.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface PickResult {
    x: number; y: number;
    rgb: [number, number, number];
    hex: string;
    hsl: [number, number, number];
}

interface State {
    fileName: string;
    imageUrl: string | null;
    picks: PickResult[];
    copiedIdx: number | null;
    nativeAvailable: boolean;
}

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0; const l = (max + min) / 2;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
            case g: h = ((b - r) / d + 2) * 60; break;
            case b: h = ((r - g) / d + 4) * 60; break;
        }
    }
    return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}

export default class EyedropperComponent extends Component<{ feature?: string }, State> {
    private canvasRef: RefObject<HTMLCanvasElement | null> = createRef();
    private imgRef: RefObject<HTMLImageElement | null> = createRef();
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        fileName: '',
        imageUrl: null,
        picks: [],
        copiedIdx: null,
        // typeof window !== 'undefined' guards SSR/prerender, where window is unavailable.
        nativeAvailable: typeof window !== 'undefined' && typeof (window as any).EyeDropper !== 'undefined',
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        this.setState({ imageUrl: url, fileName: f.name, picks: [] });
    };

    onImageLoad = () => {
        if (!this.imgRef.current || !this.canvasRef.current) return;
        const canvas = this.canvasRef.current;
        const img = this.imgRef.current;
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d', { willReadFrequently: true })!.drawImage(img, 0, 0);
    };

    onImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
        if (!this.imgRef.current || !this.canvasRef.current) return;
        const img = this.imgRef.current;
        const rect = img.getBoundingClientRect();
        const scaleX = img.naturalWidth / rect.width;
        const scaleY = img.naturalHeight / rect.height;
        const x = Math.floor((e.clientX - rect.left) * scaleX);
        const y = Math.floor((e.clientY - rect.top) * scaleY);
        const ctx = this.canvasRef.current.getContext('2d')!;
        const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
        const pick: PickResult = {
            x, y, rgb: [r, g, b], hex: rgbToHex(r, g, b), hsl: rgbToHsl(r, g, b),
        };
        this.setState({ picks: [pick, ...this.state.picks].slice(0, 12) });
    };

    nativePick = async () => {
        try {
            const ed = new (window as any).EyeDropper();
            const result = await ed.open();
            const hex = result.sRGBHex;
            const m = hex.replace('#', '').match(/^[0-9a-f]{6}$/i);
            if (!m) return;
            const v = parseInt(hex.replace('#', ''), 16);
            const rgb: [number, number, number] = [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
            const pick: PickResult = { x: 0, y: 0, rgb, hex, hsl: rgbToHsl(rgb[0], rgb[1], rgb[2]) };
            this.setState({ picks: [pick, ...this.state.picks].slice(0, 12) });
        } catch {
            // user cancelled or unsupported
        }
    };

    copyHex = (hex: string, i: number) => {
        navigator.clipboard.writeText(hex).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedIdx: i });
            this.copyTimeout = setTimeout(() => this.setState({ copiedIdx: null }), 1500);
        });
    };

    render() {
        const { fileName, imageUrl, picks, copiedIdx, nativeAvailable } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        {/* No absolutes in user-facing copy (owner decision 2026-09-01, task B5) —
                            state where processing happens, never "stays in your browser" as a promise. */}
                        <label className={sharedStyles.label}>Image (processed locally in your browser)</label>
                        <div className={styles.fileBox}>
                            <input type="file" id="eyedropper-file" accept="image/*" className={styles.fileInput}
                                onChange={this.onFileChange} />
                            <label htmlFor="eyedropper-file" className={sharedStyles.buttonPrimary}>Choose image</label>
                            {fileName && <div className={styles.fileName}>{fileName}</div>}
                        </div>
                        {nativeAvailable && (
                            <>
                                <p className={styles.divider}>or</p>
                                <button className={sharedStyles.buttonPrimary} onClick={this.nativePick}>
                                    Pick anywhere on the screen (native API)
                                </button>
                                <p className={styles.note}>Uses the browser's built-in EyeDropper. Chrome / Edge desktop only.</p>
                            </>
                        )}
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        {imageUrl && (
                            <>
                                <label className={sharedStyles.label}>Click anywhere on the image to pick a color</label>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img ref={this.imgRef} src={imageUrl} alt="Pick from this"
                                    onLoad={this.onImageLoad} onClick={this.onImageClick}
                                    className={styles.image} />
                            </>
                        )}
                        {picks.length > 0 && (
                            <>
                                <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Picked colors (latest first)</label>
                                <div className={styles.pickList}>
                                    {picks.map((p, i) => (
                                        <div key={i} className={styles.pickRow}>
                                            <span className={styles.swatch} style={{ background: p.hex }} />
                                            <div className={styles.pickInfo}>
                                                <code>{p.hex}</code>
                                                <span>rgb({p.rgb.join(', ')})</span>
                                                <span>hsl({p.hsl[0]}, {p.hsl[1]}%, {p.hsl[2]}%)</span>
                                            </div>
                                            <button className={`${sharedStyles.copyButton} ${copiedIdx === i ? sharedStyles.copyButtonSuccess : ''}`}
                                                onClick={() => this.copyHex(p.hex, i)}
                                                style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                                                {copiedIdx === i ? '✓' : 'Copy'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
                    </div>
                </div>
            </div>
        );
    }
}
